import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import { format } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";

interface Comment {
  id: string;
  blog_post_id: string;
  user_id: string;
  username: string;
  content: string;
  video?: string;
  photos?: string[];
  pdf?: string;
  created_at: string;
  approved: boolean;
}

interface CommentListProps {
  postId: string;
  onCommentDeleted?: () => void;
  onCommentApproved?: () => void;
}

const CommentList: React.FC<CommentListProps> = ({
  postId,
  onCommentDeleted,
  onCommentApproved,
}) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPhoto, setOpenPhoto] = useState<string | null>(null);
  const [openVideo, setOpenVideo] = useState<string | null>(null);
  const [openPdf, setOpenPdf] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const headers: HeadersInit = {};
      if (user && user.token) {
        headers["Authorization"] = `Bearer ${user.token}`;
      }
      const response = await fetch(`/api/blog/${postId}/comments`, { headers });
      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }
      const data = await response.json();
      setComments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [postId, user]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleApprove = async (commentId: string) => {
    if (!user || user.role !== "admin") return;

    try {
      const response = await fetch(`/api/blog/comments/${commentId}/approve`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to approve comment");
      }

      await fetchComments();
      if (onCommentApproved) onCommentApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!user) return;

    try {
      const response = await fetch(`/api/blog/comments/${commentId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete comment");
      }

      await fetchComments();
      if (onCommentDeleted) onCommentDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Comments ({comments.length})
      </Typography>
      {comments.length === 0 ? (
        <Typography color="text.secondary" sx={{ my: 2 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        comments.map((comment) => {
          const isAdmin = user && user.role === "admin";
          return (
            <Paper
              key={comment.id}
              sx={{
                p: 2,
                mb: 2,
                position: "relative",
                opacity: comment.approved ? 1 : 0.7,
              }}
            >
              {!comment.approved && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  Pending Approval
                </Typography>
              )}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="subtitle2" color="primary">
                  {comment.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(
                    new Date(comment.created_at),
                    "MMM d, yyyy 'at' h:mm a"
                  )}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mb: 2 }}
              >
                {comment.content}
              </Typography>

              {/* Media attachments */}
              {comment.video && (
                <Box sx={{ mb: 2 }}>
                  <video
                    src={comment.video}
                    controls
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      cursor: "pointer",
                    }}
                    onClick={() => setOpenVideo(comment.video!)}
                  />
                </Box>
              )}
              {comment.photos && comment.photos.length > 0 && (
                <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {comment.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo}
                      alt={`Comment attachment ${idx + 1}`}
                      style={{
                        maxWidth: 100,
                        maxHeight: 100,
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                      onClick={() => setOpenPhoto(photo)}
                    />
                  ))}
                </Box>
              )}
              {comment.pdf && (
                <Box sx={{ mb: 2 }}>
                  <a
                    href={comment.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenPdf(comment.pdf!);
                    }}
                  >
                    <DescriptionIcon color="primary" />
                    <span>View PDF</span>
                  </a>
                </Box>
              )}

              {/* Admin actions */}
              {isAdmin &&
                ((() => {
                  console.log(
                    "Rendering admin actions for comment",
                    comment.id
                  );
                  return null;
                })() || (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 1,
                      gap: 1,
                    }}
                  >
                    {!comment.approved && (
                      <>
                        <IconButton
                          color="success"
                          onClick={() => handleApprove(comment.id)}
                          size="small"
                          aria-label="Approve Comment"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(comment.id)}
                      size="small"
                      aria-label="Delete Comment"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
            </Paper>
          );
        })
      )}
      <Dialog
        open={!!openPhoto}
        onClose={() => setOpenPhoto(null)}
        maxWidth="md"
      >
        <DialogContent>
          {openPhoto && (
            <img
              src={openPhoto}
              alt="Enlarged comment attachment"
              style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!openVideo}
        onClose={() => setOpenVideo(null)}
        maxWidth="md"
      >
        <DialogContent>
          {openVideo && (
            <video
              src={openVideo}
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!openPdf}
        onClose={() => setOpenPdf(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {openPdf && (
            <iframe
              src={openPdf}
              title="PDF Preview"
              style={{ width: "100%", height: "80vh", border: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CommentList;
